import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {validateStripeSession} from '../api/stripeApi';
import {useAuthStore} from '../store/authStore';
import {logger} from '../utils/logger';

interface StripeSessionHandlerResult {
  isProcessing: boolean;
  error: string | null;
  success: boolean;
  sessionId: string | null;
  clearSuccess: () => void;
}

/**
 * A hook to handle Stripe checkout session verification
 *
 * This hook centralizes Stripe checkout session verification logic:
 * - Extracts session_id from URL query parameters
 * - Handles session verification and updates user subscription
 * - Prevents duplicate processing by managing state
 * - Cleans up URL parameters after processing
 * - Returns state for UI feedback (loading, success, error)
 */
export function useStripeSessionHandler(): StripeSessionHandlerResult {
  const location = useLocation();
  const {user, updateSubscription} = useAuthStore();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState<boolean>(false);

  // Function to clear the success state
  const clearSuccess = () => {
    setSuccess(false);
  };

  // Extract session ID from URL and clean it up
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');

    if (sid && !hasProcessed) {
      setSessionId(sid);

      // Clean URL by removing query parameters without page reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location, hasProcessed]);

  // Process Stripe session when we have a sessionId and user
  useEffect(() => {
    const verifyCheckoutSession = async () => {
      // Only process if we have all required data and haven't processed yet
      if (!sessionId || !user || hasProcessed || isProcessing) {
        return;
      }

      try {
        setIsProcessing(true);
        setError(null);

        logger.info('Verifying Stripe checkout session:', sessionId);
        const result = await validateStripeSession(sessionId);

        if (result.valid && result.subscription) {
          // Update user subscription in the store
          await updateSubscription(user.uid, {
            plan: result.subscription.plan,
            billing: result.subscription.billing,
            status: 'active',
            stripeCustomerId: result.subscription.customerId,
            stripeSubscriptionId: result.subscription.subscriptionId,
            currentPeriodEnd: result.subscription.currentPeriodEnd,
          });

          logger.info('Subscription updated successfully');
          setSuccess(true);
        } else {
          setError(result.error || 'Failed to validate subscription');
          console.error('Subscription validation failed:', result.error);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error processing checkout';
        setError(errorMessage);
        console.error('Error processing Stripe session:', err);
      } finally {
        setIsProcessing(false);
        setHasProcessed(true);
      }
    };

    verifyCheckoutSession();
  }, [sessionId, user, updateSubscription, hasProcessed, isProcessing]);

  return {
    isProcessing,
    error,
    success,
    sessionId,
    clearSuccess,
  };
}
